import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const RoleBasedEntryPoints = () => {
  const roles = [
    {
      id: 'cto',
      title: 'Chief Technology Officer',
      subtitle: 'Strategic Technology Leadership',
      painPoints: [
        'Delivery velocity not matching business demands',
        'Technical debt slowing innovation cycles',
        'Engineering teams struggling with ownership'
      ],
      valueProps: [
        'Transform engineering culture and velocity',
        'Align technical strategy with business goals',
        'Build scalable architectural foundations'
      ],
      icon: 'Crown',
      color: 'from-purple-600 to-purple-800',
      accentColor: 'purple-500'
    },
    {
      id: 'vp-engineering',
      title: 'VP of Engineering',
      subtitle: 'Operational Excellence & Team Leadership',
      painPoints: [
        'Caught between executive pressure and team reality',
        'Process overhead without velocity gains',
        'Difficulty scaling team productivity'
      ],
      valueProps: [
        'Embed execution expertise within teams',
        'Eliminate process waste and ceremony overhead',
        'Build ownership culture that scales'
      ],
      icon: 'Users',
      color: 'from-blue-600 to-blue-800',
      accentColor: 'blue-500'
    },
    {
      id: 'head-platform',
      title: 'Head of Platform',
      subtitle: 'Architecture & Infrastructure Leadership',
      painPoints: [
        'Legacy systems constraining innovation',
        'Complex architectural decisions and trade-offs',
        'Platform adoption and developer experience'
      ],
      valueProps: [
        'Modernize architecture with minimal disruption',
        'Build platforms that developers love to use',
        'Create scalable, maintainable system designs'
      ],
      icon: 'Layers',
      color: 'from-green-600 to-green-800',
      accentColor: 'green-500'
    }
  ];

  return (
    <section className="py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            Built for Engineering Leaders
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Choose your role to discover how SIGNA transforms engineering organizations like yours
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {roles.map((role, index) => (
            <motion.div
              key={role.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <Link to="/discovery-portal" className="block">
                <div className="bg-white rounded-2xl p-8 h-full border border-border hover:border-accent/30 transition-all duration-300 hover:elevation-moderate">
                  {/* Header */}
                  <div className="mb-6">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${role.color} flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300`}>
                      <Icon name={role.icon} size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-2">{role.title}</h3>
                    <p className="text-sm text-text-secondary">{role.subtitle}</p>
                  </div>

                  {/* Pain Points */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-primary mb-3 flex items-center">
                      <Icon name="AlertTriangle" size={16} className="mr-2 text-warning" />
                      Current Challenges
                    </h4>
                    <ul className="space-y-2">
                      {role.painPoints.map((point, idx) => (
                        <li key={idx} className="text-sm text-text-secondary flex items-start">
                          <div className="w-1.5 h-1.5 bg-warning rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Value Props */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-primary mb-3 flex items-center">
                      <Icon name="CheckCircle" size={16} className="mr-2 text-success" />
                      SIGNA Solutions
                    </h4>
                    <ul className="space-y-2">
                      {role.valueProps.map((prop, idx) => (
                        <li key={idx} className="text-sm text-text-secondary flex items-start">
                          <div className="w-1.5 h-1.5 bg-success rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          {prop}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <span className="text-sm font-medium text-accent">Explore Solutions</span>
                    <Icon name="ArrowRight" size={16} className="text-accent group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RoleBasedEntryPoints;
